import { useSuspenseQuery } from "@tanstack/react-query";
import { useState } from "react";
import { GqlTransport } from "shared/api/gqlx";
import { NestedTopicTree, TopicNode } from "shared/components/organization/topics/TopicNode";
import { topicsQueryOptions } from "shared/graphql/topics";
import { to } from "shared/utils/fns";
import { TopicLikeFragment } from "src/gql/graphql";

function buildParentMap(rootTopic: NestedTopicTree) {
    const parents = new Map<string, string | null>();
    function walk(node: NestedTopicTree, parentId: string | null) {
        parents.set(node.id, parentId);
        node.children?.forEach((child) => walk(child, node.id));
    }
    walk(rootTopic, null);
    return parents;
}

function buildRequiredIdSet(rootTopic: NestedTopicTree) {
    const required = new Set<string>();
    function collect(node: NestedTopicTree) {
        if (node.isRequired) required.add(node.id);
        node.children?.forEach(collect);
    }
    collect(rootTopic);
    return required;
}

export function UserSubscribeTopicNode(props: {
    defaultCheckedTopics: TopicLikeFragment[];
    checkboxOnChange?: (topic: TopicLikeFragment, checked: boolean) => void | Promise<unknown>;
    customerId?: string;
    transport?: GqlTransport;
}) {
    const {
        data: { rootTopic },
    } = useSuspenseQuery(topicsQueryOptions.theWholeTree(props.transport));

    const parentById = buildParentMap(rootTopic);
    const requiredIds = buildRequiredIdSet(rootTopic);

    const [userSelectedIds, setUserSelectedIds] = useState<Set<string>>(() => new Set(props.defaultCheckedTopics.map(to("id"))));

    const selectedIds = new Set([...userSelectedIds, ...requiredIds]);

    function hasSelectedAncestor(topicId: string): boolean {
        const parentId = parentById.get(topicId);
        if (!parentId) return false;
        return selectedIds.has(parentId) || hasSelectedAncestor(parentId);
    }

    function isChecked(topic: TopicLikeFragment) {
        return selectedIds.has(topic.id) || hasSelectedAncestor(topic.id);
    }

    function isDisabled(topic: TopicLikeFragment) {
        return topic.isRequired || hasSelectedAncestor(topic.id);
    }

    function handleChange(topic: TopicLikeFragment, checked: boolean) {
        if (isDisabled(topic)) return;

        const next = new Set(userSelectedIds);
        checked ? next.add(topic.id) : next.delete(topic.id);
        setUserSelectedIds(next);

        void props.checkboxOnChange?.(topic, checked);
    }

    return rootTopic.children?.map((c) => (
        <TopicNode
            key={c.id}
            topic={c}
            isChecked={isChecked}
            initialExpandedNodes={rootTopic.children.filter((t) => t.isCategory).map(to("id"))}
            shouldRenderCheckbox={() => true}
            checkboxOnChange={(t, checked) => handleChange(t, checked)}
            isDisabled={isDisabled}
            disabledTooltipLabel={(topic) =>
                topic.isRequired
                    ? "This topic is required and cannot be unsubscribed from"
                    : hasSelectedAncestor(topic.id)
                      ? `This ${topic.isCategory ? "category" : "topic"} is automatically selected because its parent category is selected`
                      : undefined
            }
        />
    ));
}
